import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ideaAdminCreateSchema, matryoshkaSchema, type IdeaAdminCreate } from '@masha/shared';
import { api, ApiError, type AdminIdeaDetail } from '../../lib/api';
import { AutoTextarea } from '../../components/ui';

function mapDetailToForm(d: AdminIdeaDetail): IdeaAdminCreate {
  return {
    formatId: d.format_id,
    code: d.code,
    title: d.title,
    description: d.description,
    audienceAction: d.audience_action,
    matryoshka: d.matryoshka,
    thumbnailUrl: d.thumbnail_url,
    previewVideoUrls: d.preview_video_urls,
    systemPrompt: d.system_prompt,
    questions: d.questions,
    isActive: d.is_active,
    sortOrder: d.sort_order,
    marketingGoalIds: d.marketing_goal_ids,
  };
}

const DEFAULT_VALUES: IdeaAdminCreate = {
  formatId: 1,
  code: '',
  title: '',
  description: '',
  audienceAction: null,
  matryoshka: 'личность',
  thumbnailUrl: '',
  previewVideoUrls: [],
  systemPrompt: '',
  questions: [],
  isActive: true,
  sortOrder: 0,
  marketingGoalIds: [],
};

export function AdminIdeasFormPage() {
  const params = useParams<{ id?: string }>();
  const editingId = params.id ? Number(params.id) : null;
  const isEdit = editingId !== null;

  const navigate = useNavigate();
  const qc = useQueryClient();

  const ideaQuery = useQuery({
    queryKey: ['admin', 'idea', editingId],
    queryFn: () => api.adminGetIdea(editingId as number),
    enabled: isEdit,
  });

  const goalsQuery = useQuery({
    queryKey: ['admin', 'goals'],
    queryFn: () => api.adminListGoals(),
  });

  const formatsQuery = useQuery({
    queryKey: ['admin', 'formats'],
    queryFn: () => api.adminListFormats(),
    staleTime: 5 * 60_000,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IdeaAdminCreate>({
    resolver: zodResolver(ideaAdminCreateSchema) as never,
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
  } = useFieldArray({ control, name: 'questions' });

  useEffect(() => {
    if (ideaQuery.data) {
      reset(mapDetailToForm(ideaQuery.data.idea));
    }
  }, [ideaQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (body: IdeaAdminCreate) =>
      isEdit ? api.adminUpdateIdea(editingId as number, body) : api.adminCreateIdea(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ideas'] });
      if (isEdit) {
        qc.invalidateQueries({ queryKey: ['admin', 'idea', editingId] });
      }
      navigate('/admin/ideas');
    },
  });

  const previewVideoUrls = watch('previewVideoUrls') ?? [];

  const uploadVideo = useMutation({
    mutationFn: (file: File) => api.adminUploadVideo(file),
    onSuccess: ({ url }) => {
      const current = watch('previewVideoUrls') ?? [];
      setValue('previewVideoUrls', [...current, url], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
  });

  const removeVideo = (idx: number) => {
    const current = watch('previewVideoUrls') ?? [];
    setValue(
      'previewVideoUrls',
      current.filter((_, i) => i !== idx),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const uploadThumb = useMutation({
    mutationFn: (file: File) => api.adminUploadThumbnail(file),
    onSuccess: ({ url }) =>
      setValue('thumbnailUrl', url, { shouldValidate: true, shouldDirty: true }),
  });

  const thumbnailUrl = watch('thumbnailUrl');
  const formatId = watch('formatId');
  const formats = formatsQuery.data?.formats ?? [];
  const isCarousel = formats.find((f) => f.id === formatId)?.code === 'carousel';

  if (isEdit && ideaQuery.isLoading) {
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto">
        <p className="text-sm opacity-60 text-center py-8">Загрузка...</p>
      </div>
    );
  }

  if (isEdit && ideaQuery.error) {
    const err = ideaQuery.error;
    const notFound = err instanceof ApiError && err.status === 404;
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto">
        <p className="text-center py-8 text-sm opacity-70">
          {notFound ? 'Идея не найдена' : 'Не удалось загрузить идею'}
        </p>
        <div className="text-center">
          <Link
            to="/admin/ideas"
            className="inline-block px-4 py-2 rounded-xl bg-tg-button text-tg-button-text text-sm"
          >
            ← К списку
          </Link>
        </div>
      </div>
    );
  }

  const errorEntries = Object.entries(errors).filter(
    ([, e]) => e && (e as { message?: string }).message,
  );

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto">
      <header className="mb-6">
        <Link to="/admin/ideas" className="text-sm opacity-70">
          ← К списку
        </Link>
        <h1 className="text-xl font-bold mt-2">{isEdit ? 'Редактировать идею' : 'Новая идея'}</h1>
      </header>

      <form
        onSubmit={handleSubmit((data) =>
          saveMutation.mutate(isCarousel ? { ...data, previewVideoUrls: [] } : data),
        )}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium mb-1">Формат</label>
          {/* Controlled on purpose: formats and the idea load independently, and a
              controlled <select> re-applies the value once the options arrive. */}
          <Controller
            control={control}
            name="formatId"
            render={({ field }) => (
              <select
                className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm"
                disabled={formats.length === 0}
                value={field.value}
                onChange={(e) => field.onChange(Number(e.target.value))}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              >
                {formats.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.is_active ? '' : ' (выключен)'}
                  </option>
                ))}
              </select>
            )}
          />
          {isCarousel && (
            <p className="text-xs opacity-60 mt-1">
              Для каруселей видео не нужно: слайды рендерит движок.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Code (slug)</label>
          <input
            type="text"
            maxLength={64}
            className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm lowercase"
            {...register('code')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Название</label>
          <input
            type="text"
            className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm"
            {...register('title')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Описание</label>
          <AutoTextarea minRows={3} {...register('description')} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Действие аудитории (опционально)</label>
          <Controller
            control={control}
            name="audienceAction"
            render={({ field }) => (
              <input
                type="text"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm"
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Матрёшка</label>
          <select
            className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm"
            {...register('matryoshka')}
          >
            {matryoshkaSchema.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Цели маркетинга</label>
          {goalsQuery.isLoading && <p className="text-xs opacity-60">Загрузка целей...</p>}
          {goalsQuery.data && goalsQuery.data.goals.length === 0 && (
            <p className="text-xs opacity-60">Целей пока нет</p>
          )}
          {goalsQuery.data && goalsQuery.data.goals.length > 0 && (
            <Controller
              control={control}
              name="marketingGoalIds"
              render={({ field }) => {
                const selected = field.value ?? [];
                const toggle = (id: number) => {
                  if (selected.includes(id)) {
                    field.onChange(selected.filter((v) => v !== id));
                  } else {
                    field.onChange([...selected, id]);
                  }
                };
                return (
                  <div className="flex flex-wrap gap-2">
                    {goalsQuery.data.goals.map((g) => {
                      const active = selected.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => toggle(g.id)}
                          className={
                            'px-3 py-1 rounded-full text-xs border ' +
                            (active
                              ? 'bg-tg-button text-tg-button-text border-transparent'
                              : 'bg-tg-section-bg border-tg-hint opacity-70 hover:opacity-100')
                          }
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                );
              }}
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Обложка</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={uploadThumb.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadThumb.mutate(file);
              e.target.value = '';
            }}
            className="text-sm"
          />
          {uploadThumb.isPending && <p className="text-xs opacity-60 mt-1">Загрузка...</p>}
          {uploadThumb.error && (
            <div className="text-sm text-red-500 mt-1">{uploadThumb.error.message}</div>
          )}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt="thumbnail"
              className="h-24 w-24 object-cover rounded mt-2"
            />
          )}
          <input type="hidden" {...register('thumbnailUrl')} />
        </div>

        {!isCarousel && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Видео-превью ({previewVideoUrls.length})
            </label>
            {previewVideoUrls.length > 0 && (
              <div className="space-y-2 mb-2">
                {previewVideoUrls.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="flex items-start gap-2">
                    <video
                      src={url}
                      controls
                      className="w-full max-w-xs rounded bg-black aspect-[9/16] object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeVideo(idx)}
                      className="px-2 py-1 rounded-lg bg-tg-section-bg border border-tg-hint text-sm opacity-70 hover:opacity-100"
                      aria-label="Удалить видео"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              disabled={uploadVideo.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadVideo.mutate(file);
                e.target.value = '';
              }}
              className="text-sm"
            />
            {uploadVideo.isPending && <p className="text-xs opacity-60 mt-1">Загрузка...</p>}
            {uploadVideo.error && (
              <div className="text-sm text-red-500 mt-1">{uploadVideo.error.message}</div>
            )}
            <p className="text-xs opacity-60 mt-1">
              Выберите видео-файл, и он добавится к карусели. Можно загрузить несколько.
            </p>
          </div>
        )}

        <details className="rounded-xl bg-tg-section-bg p-3">
          <summary className="text-sm font-medium cursor-pointer">Advanced: system_prompt</summary>
          <AutoTextarea
            minRows={6}
            maxRows={40}
            className="mt-2 bg-tg-bg font-mono"
            {...register('systemPrompt')}
          />
        </details>

        <div>
          <label className="block text-sm font-medium mb-1">Вопросы</label>
          <div className="space-y-3">
            {questionFields.map((field, index) => (
              <div
                key={field.id}
                className="border border-tg-hint/30 rounded-xl p-3 bg-tg-section-bg/40"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <div>
                      <label className="block text-xs font-medium opacity-70 mb-1">
                        Q{index + 1}: Заголовок (жирный)
                      </label>
                      <AutoTextarea
                        minRows={1}
                        {...register(`questions.${index}.title` as const)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium opacity-70 mb-1">
                        Описание / уточнение (опционально)
                      </label>
                      <Controller
                        control={control}
                        name={`questions.${index}.description` as const}
                        render={({ field: descField }) => (
                          <AutoTextarea
                            minRows={2}
                            placeholder="Уточнения, подсказки, примеры..."
                            value={descField.value ?? ''}
                            onChange={(e) =>
                              descField.onChange(e.target.value === '' ? null : e.target.value)
                            }
                            onBlur={descField.onBlur}
                            name={descField.name}
                            ref={descField.ref}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(index)}
                    className="px-2 py-1 rounded-lg bg-tg-section-bg border border-tg-hint text-sm opacity-70 hover:opacity-100"
                    aria-label="Удалить вопрос"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => appendQuestion({ title: '', description: null })}
              className="px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm opacity-70 hover:opacity-100"
            >
              + Добавить вопрос
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input id="isActive" type="checkbox" {...register('isActive')} />
          <label htmlFor="isActive" className="text-sm">
            Активна (видна юзерам)
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Порядок сортировки</label>
          <input
            type="number"
            className="w-full px-3 py-2 rounded-xl bg-tg-section-bg border border-tg-hint text-sm"
            {...register('sortOrder', { valueAsNumber: true })}
          />
        </div>

        {errorEntries.length > 0 && (
          <ul className="text-sm text-red-500 list-disc pl-5">
            {errorEntries.map(([key, err]) => (
              <li key={key}>
                {key}: {(err as { message?: string }).message}
              </li>
            ))}
          </ul>
        )}

        {saveMutation.error && (
          <div className="text-sm text-red-500">{saveMutation.error.message}</div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={saveMutation.isPending || isSubmitting}
            className="flex-1 px-4 py-3 rounded-xl bg-tg-button text-tg-button-text text-sm font-medium disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Сохранение...' : 'Сохранить'}
          </button>
          <Link
            to="/admin/ideas"
            className="px-4 py-3 rounded-xl bg-tg-section-bg border border-tg-hint text-sm opacity-70 hover:opacity-100"
          >
            Отмена
          </Link>
        </div>
      </form>
    </div>
  );
}
